import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X, Wand2, ArrowRight } from 'lucide-react';
import { settingsApi } from '../../../api/endpoints/settings';
import { translationsApi } from '../../../api/endpoints/translations';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Badge } from '../../../components/ui/Badge';
import { useToast } from '../../../components/ui/Alert';
import { LANGUAGES, getLanguageLabel } from '../../../lib/constants';
import type { StoreSettings } from '../../../types/settings';

/** Map language codes to country codes for flag images */
const LANG_TO_COUNTRY: Record<string, string> = {
  ar: 'sa', en: 'us', fr: 'fr', es: 'es', de: 'de', tr: 'tr',
  ur: 'pk', fa: 'ir', hi: 'in', id: 'id', ms: 'my', pt: 'br',
  it: 'it', nl: 'nl', ru: 'ru', zh: 'cn', ja: 'jp', ko: 'kr',
  th: 'th', vi: 'vn', he: 'il',
};

const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur']);

function getFlagUrl(langCode: string): string {
  const country = LANG_TO_COUNTRY[langCode] ?? langCode;
  return `https://flagcdn.com/24x18/${country}.png`;
}

interface Props { settings?: StoreSettings; storeId: string; }

export function LanguagesTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [defaultLang, setDefaultLang] = useState('ar');
  const [enabledLangs, setEnabledLangs] = useState<string[]>(['ar']);
  const [addLang, setAddLang] = useState('');
  const [uiSourceLang, setUiSourceLang] = useState('');
  const [uiTargetLang, setUiTargetLang] = useState('');
  const [contentSourceLang, setContentSourceLang] = useState('');
  const [contentTargetLang, setContentTargetLang] = useState('');

  // Fetch supported languages from backend, fall back to hardcoded list
  const { data: backendLanguages } = useQuery({
    queryKey: ['supportedLanguages', storeId],
    queryFn: () => settingsApi.getSupportedLanguages(storeId),
    staleTime: 1000 * 60 * 30, // 30 minutes – this list rarely changes
    retry: 1,
  });

  /** Build a label lookup and ordered code list from backend or fallback */
  const { allLanguageCodes, labelOf } = useMemo(() => {
    if (backendLanguages && backendLanguages.length > 0) {
      const lookup: Record<string, string> = {};
      for (const lang of backendLanguages) {
        lookup[lang.code] =
          lang.native === lang.english
            ? lang.english
            : `${lang.native} (${lang.english})`;
      }
      return {
        allLanguageCodes: backendLanguages.map((l) => l.code),
        labelOf: (code: string) => lookup[code] || getLanguageLabel(code) || code,
      };
    }
    // Fallback to constants
    return {
      allLanguageCodes: LANGUAGES as unknown as string[],
      labelOf: (code: string) => getLanguageLabel(code),
    };
  }, [backendLanguages]);

  useEffect(() => {
    if (settings) {
      setDefaultLang(settings.language || 'ar');
      setEnabledLangs(
        (settings as StoreSettings & { enabledLanguages?: string[] }).enabledLanguages ?? [settings.language || 'ar'],
      );
    }
  }, [settings]);

  // Fetch translation progress stats
  const { data: translationStats } = useQuery({
    queryKey: ['translations-stats', storeId],
    queryFn: () => translationsApi.getStats(storeId),
    enabled: !!storeId && enabledLangs.length >= 2,
    staleTime: 1000 * 60 * 5,
  });

  const translationProgress = useMemo(() => {
    const progress: Record<string, number> = {};
    if (translationStats?.stats) {
      for (const [lang, stat] of Object.entries(translationStats.stats)) {
        progress[lang] = Math.round(stat.percentage ?? 0);
      }
    }
    return progress;
  }, [translationStats]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['settings', storeId] });

  const addMutation = useMutation({
    mutationFn: (language: string) => settingsApi.addLanguage(storeId, language),
    onSuccess: () => { invalidate(); toast('success', t('common.success')); setAddLang(''); },
    onError: () => toast('error', t('common.error')),
  });

  const removeMutation = useMutation({
    mutationFn: (language: string) => settingsApi.removeLanguage(storeId, language),
    onSuccess: () => { invalidate(); toast('success', t('common.success')); },
    onError: () => toast('error', t('common.error')),
  });

  const setDefaultMutation = useMutation({
    mutationFn: (language: string) => settingsApi.setDefaultLanguage(storeId, language),
    onSuccess: () => { invalidate(); toast('success', t('common.success')); },
    onError: () => toast('error', t('common.error')),
  });

  const translateUiMutation = useMutation({
    mutationFn: () =>
      translationsApi.bulkTranslate(storeId, {
        entityType: 'ui',
        sourceLanguage: uiSourceLang,
        targetLanguage: uiTargetLang,
      }),
    onSuccess: (result) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['translations', storeId] });
      toast('success', t('settingsLanguages.uiTranslateSuccess', { count: result.translated }));
    },
    onError: () => toast('error', t('common.error')),
  });

  const translateContentMutation = useMutation({
    mutationFn: () =>
      translationsApi.bulkTranslate(storeId, {
        entityType: 'content',
        sourceLanguage: contentSourceLang,
        targetLanguage: contentTargetLang,
      }),
    onSuccess: (result) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['translations', storeId] });
      toast('success', t('settingsLanguages.contentTranslateSuccess', { count: result.translated }));
    },
    onError: () => toast('error', t('common.error')),
  });

  const availableToAdd = allLanguageCodes.filter(l => !enabledLangs.includes(l));

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Default language */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-4">{t('settings.defaultLanguage')}</h2>
        <div className="flex items-end gap-3">
          <img
            src={getFlagUrl(defaultLang)}
            alt=""
            width={24}
            height={18}
            className="mb-3 rounded-sm object-cover"
            loading="lazy"
          />
          <div className="flex-1">
            <Select
              label={t('settings.defaultLanguage')}
              options={enabledLangs.map((l) => ({ value: l, label: labelOf(l) }))}
              value={defaultLang}
              onChange={(e) => {
                setDefaultLang(e.target.value);
                setDefaultMutation.mutate(e.target.value);
              }}
            />
          </div>
        </div>
      </section>

      {/* Enabled languages */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-4">{t('settings.enabledLanguages')}</h2>
        <div className="space-y-2">
          {enabledLangs.map((lang) => (
            <div
              key={lang}
              className="rounded-lg border border-border-default px-4 py-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <img
                    src={getFlagUrl(lang)}
                    alt=""
                    width={24}
                    height={18}
                    className="rounded-sm object-cover"
                    loading="lazy"
                  />
                  <span className="text-sm font-medium">{labelOf(lang)}</span>
                  {lang === defaultLang && <Badge variant="accent">{t('settings.default')}</Badge>}
                  {RTL_LANGUAGES.has(lang) && <Badge variant="info">RTL</Badge>}
                </div>
                {lang !== defaultLang && (
                  <button
                    onClick={() => removeMutation.mutate(lang)}
                    disabled={removeMutation.isPending || enabledLangs.length <= 1}
                    className="rounded-lg p-1.5 text-text-muted hover:bg-danger/10 hover:text-danger transition-colors disabled:opacity-30"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* Translation progress bar */}
              {lang !== defaultLang && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs text-text-muted mb-1">
                    <span>{t('settingsLanguages.translationProgress', 'Translation progress')}</span>
                    <span>{translationProgress[lang] ?? 0}%</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-bg-tertiary overflow-hidden">
                    <div
                      className="h-full rounded-full bg-accent transition-all"
                      style={{ width: `${translationProgress[lang] ?? 0}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add language */}
        {availableToAdd.length > 0 && (
          <div className="flex items-end gap-3 pt-2 border-t border-border-default">
            {addLang && (
              <img
                src={getFlagUrl(addLang)}
                alt=""
                width={24}
                height={18}
                className="mb-3 rounded-sm object-cover"
                loading="lazy"
              />
            )}
            <div className="flex-1">
              <Select
                label={t('settingsLanguages.addLanguage', 'Add Language')}
                options={[
                  { value: '', label: t('settingsLanguages.selectLanguage', 'Select...') },
                  ...availableToAdd.map((l) => ({ value: l, label: labelOf(l) })),
                ]}
                value={addLang}
                onChange={(e) => setAddLang(e.target.value)}
              />
            </div>
            <Button
              onClick={() => addLang && addMutation.mutate(addLang)}
              isLoading={addMutation.isPending}
              disabled={!addLang}
              size="sm"
            >
              <Plus className="h-4 w-4" />
              {t('common.add')}
            </Button>
          </div>
        )}
      </section>

      {/* Translation Tools */}
      {enabledLangs.length >= 2 && (
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-5">
          <div>
            <h2 className="text-base font-semibold">{t('settingsLanguages.translationTools', 'Translation Tools')}</h2>
            <p className="text-sm text-text-muted mt-1">
              {t('settingsLanguages.translationToolsDesc', 'Use AI to automatically translate your store texts and content between languages.')}
            </p>
          </div>

          {/* Auto-Translate UI Texts */}
          <div className="rounded-lg border border-border-default p-4 space-y-3">
            <div>
              <h3 className="text-sm font-medium">{t('settingsLanguages.translateUiTexts', 'Auto-Translate UI Texts')}</h3>
              <p className="text-xs text-text-muted mt-0.5">
                {t('settingsLanguages.translateUiTextsDesc', 'Translate all interface text strings such as button labels, headings, and messages.')}
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[140px]">
                <Select
                  label={t('settingsLanguages.sourceLanguage', 'Source Language')}
                  options={[
                    { value: '', label: t('settingsLanguages.selectLanguage', 'Select...') },
                    ...enabledLangs.map((l) => ({ value: l, label: labelOf(l) })),
                  ]}
                  value={uiSourceLang}
                  onChange={(e) => {
                    setUiSourceLang(e.target.value);
                    if (e.target.value === uiTargetLang) setUiTargetLang('');
                  }}
                />
              </div>
              <ArrowRight className="h-4 w-4 text-text-muted mb-3 shrink-0" />
              <div className="flex-1 min-w-[140px]">
                <Select
                  label={t('settingsLanguages.targetLanguage', 'Target Language')}
                  options={[
                    { value: '', label: t('settingsLanguages.selectLanguage', 'Select...') },
                    ...enabledLangs
                      .filter((l) => l !== uiSourceLang)
                      .map((l) => ({ value: l, label: labelOf(l) })),
                  ]}
                  value={uiTargetLang}
                  onChange={(e) => setUiTargetLang(e.target.value)}
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => translateUiMutation.mutate()}
                isLoading={translateUiMutation.isPending}
                disabled={!uiSourceLang || !uiTargetLang}
              >
                <Wand2 className="h-4 w-4" />
                {t('settingsLanguages.translateUiBtn', 'Translate UI Texts')}
              </Button>
            </div>
          </div>

          {/* Auto-Translate Content */}
          <div className="rounded-lg border border-border-default p-4 space-y-3">
            <div>
              <h3 className="text-sm font-medium">{t('settingsLanguages.translateContent', 'Auto-Translate Content')}</h3>
              <p className="text-xs text-text-muted mt-0.5">
                {t('settingsLanguages.translateContentDesc', 'Translate all section and product content including titles, descriptions, and details.')}
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[140px]">
                <Select
                  label={t('settingsLanguages.sourceLanguage', 'Source Language')}
                  options={[
                    { value: '', label: t('settingsLanguages.selectLanguage', 'Select...') },
                    ...enabledLangs.map((l) => ({ value: l, label: labelOf(l) })),
                  ]}
                  value={contentSourceLang}
                  onChange={(e) => {
                    setContentSourceLang(e.target.value);
                    if (e.target.value === contentTargetLang) setContentTargetLang('');
                  }}
                />
              </div>
              <ArrowRight className="h-4 w-4 text-text-muted mb-3 shrink-0" />
              <div className="flex-1 min-w-[140px]">
                <Select
                  label={t('settingsLanguages.targetLanguage', 'Target Language')}
                  options={[
                    { value: '', label: t('settingsLanguages.selectLanguage', 'Select...') },
                    ...enabledLangs
                      .filter((l) => l !== contentSourceLang)
                      .map((l) => ({ value: l, label: labelOf(l) })),
                  ]}
                  value={contentTargetLang}
                  onChange={(e) => setContentTargetLang(e.target.value)}
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => translateContentMutation.mutate()}
                isLoading={translateContentMutation.isPending}
                disabled={!contentSourceLang || !contentTargetLang}
              >
                <Wand2 className="h-4 w-4" />
                {t('settingsLanguages.translateContentBtn', 'Translate Content')}
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
